import { EmptyState, FlexColumn, FlexRow, Loading, MaterialIcon, useModal } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StatefulMenu } from 'baseui/menu';
import { StatefulPopover } from 'baseui/popover';
import { LabelMedium, ParagraphMedium } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import {
  internalNotificationTypeState,
  notificationIdsState,
  notificationsCursorPageState,
  selectedNotificationTypeState,
} from '../../storage/notifications';
import ConfirmModal from '../shared/ConfirmModal';
import { StyledTooltip } from '../ui/StyledTooltip';
import NotificationFilters from './NotificationFilters';
import NotificationItem from './NotificationItem';
import { useNotification } from './hooks/useNotification';

type Props = {
  onClose: () => void;
};
export function NotificationList({ onClose }: Readonly<Props>) {
  const { css, theme, classes } = useStyles();
  const notificationIds = useRecoilValue(notificationIdsState);
  const notificationCursorPage = useRecoilValue(notificationsCursorPageState);
  const [internalNotificationType, setInternalNotificationType] = useRecoilState(internalNotificationTypeState);
  const notificationType = useRecoilValue(selectedNotificationTypeState(internalNotificationType));
  const [loading, setLoading] = useState(false);
  const [loadingNotification, setLoadingNotification] = useState(false);
  const [nextPage, setNextPage] = useState(false);
  const { loadNotificationsList, markNotifications, getRoute } = useNotification();
  const [initialLoad, setInitialLoad] = useState(true);
  const [reloadList, setReloadList] = useState(false);
  const { openModal, closeModal, showModal } = useModal();
  const navigate = useNavigate();
  const { showFailFeedback } = useFeedback();

  const itemCount = notificationCursorPage?.hasNext
    ? (notificationIds?.length ?? 0) + 1
    : (notificationIds?.length ?? 0);

  const isItemLoaded = (index: number) => !notificationCursorPage?.hasNext || index < (notificationIds?.length || 0);

  const loadMoreItems =
    loadingNotification || !notificationCursorPage?.hasNext
      ? () => {}
      : () => {
          setNextPage(true);
        };

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
      return;
    }
    setLoading(true);
    const loadNotifications = async () => {
      const response = await loadNotificationsList(notificationType);
      if (response?.error) {
        showFailFeedback(response.error || 'Ocurrió un error al cargar las notificaciones, inténtalo más tarde.');
      }
      setLoading(false);
    };
    loadNotifications();
  }, [notificationType, reloadList]);

  useUpdateEffect(() => {
    if (!nextPage || !notificationCursorPage?.hasNext) return;

    setLoadingNotification(true);
    const loadNotifications = async () => {
      const response = await loadNotificationsList(notificationType, nextPage);
      if (response?.error) {
        showFailFeedback(response.error || 'Ocurrió un error al cargar las notificaciones, inténtalo más tarde.');
      }
      setNextPage(false);
      setLoadingNotification(false);
    };
    loadNotifications();
  }, [nextPage]);

  const onItemSelect = async (item: any, close: () => void) => {
    if (item.id === 'markAllRead') {
      await markAllNotifications('read');
    } else if (item.id === 'discardAllRead') {
      await markAllNotifications('discard_read');
    } else if (item.id === 'discardAll') {
      openModal();
    } else if (item.id === 'settings') {
      const route = getRoute('1');
      if (route) navigate(route, { replace: false });
      onClose();
    }
    close();
  };

  const markAllNotifications = async (action: string) => {
    const response = await markNotifications(action);
    if (response?.error) {
      showFailFeedback(response.error || 'No se pudo realizar esta acción, inténtalo más tarde.');
    } else {
      setReloadList((prev) => !prev);
    }
  };

  const onDeleteAll = async () => {
    await markAllNotifications('discard');
    closeModal();
  };

  return (
    <FlexColumn
      gap={0}
      classNames={css({ height: '100%' })}
    >
      <FlexRow
        classNames={`${css({
          backgroundColor: theme.colors.backgroundInversePrimary,
          padding: theme.sizing.scale500,
          color: theme.colors.white,
          alignItems: 'center',
          justifyContent: 'space-between',
        })}`}
      >
        <LabelMedium color={theme.colors.contentInversePrimary}>Notificaciones</LabelMedium>
        <FlexRow>
          <StyledTooltip
            content={'Actualizar información'}
            showArrow={false}
          >
            <Button
              size="compact"
              shape="square"
              onClick={() => setReloadList((prev) => !prev)}
              kind="primary"
            >
              <MaterialIcon
                name="refresh"
                color={theme.colors.contentInversePrimary}
              />
            </Button>
          </StyledTooltip>
          <StatefulPopover
            content={({ close }) => (
              <StatefulMenu
                onItemSelect={({ item }) => onItemSelect(item, close)}
                items={[
                  {
                    id: 'markAllRead',
                    label: (
                      <FlexRow
                        classNames={classes.centeredStart}
                        gap={theme.sizing.scale400}
                      >
                        <MaterialIcon name="check_circle" />
                        Marcar todas como leídas
                      </FlexRow>
                    ),
                  },
                  {
                    id: 'discardAllRead',
                    label: (
                      <FlexRow
                        classNames={classes.centeredStart}
                        gap={theme.sizing.scale400}
                      >
                        <MaterialIcon name="remove_circle" />
                        Descartar notificaciones leídas
                      </FlexRow>
                    ),
                  },
                  {
                    id: 'discardAll',
                    label: (
                      <FlexRow
                        classNames={classes.centeredStart}
                        gap={theme.sizing.scale400}
                      >
                        <MaterialIcon name="delete" />
                        Descartar todas
                      </FlexRow>
                    ),
                  },
                  {
                    id: 'settings',
                    label: (
                      <FlexRow
                        classNames={classes.centeredStart}
                        gap={theme.sizing.scale400}
                      >
                        <MaterialIcon name="settings" />
                        Configuración
                      </FlexRow>
                    ),
                  },
                ]}
              />
            )}
            placement={'bottomRight'}
            triggerType={'click'}
          >
            <span>
              <Button
                size="compact"
                kind="primary"
                shape="square"
              >
                <MaterialIcon
                  name="more_vert"
                  color={theme.colors.contentInversePrimary}
                />
              </Button>
            </span>
          </StatefulPopover>
        </FlexRow>
      </FlexRow>
      <FlexColumn classNames={css({ padding: theme.sizing.scale600, flex: '1' })}>
        {!notificationIds && !loading && <EmptyState title="No hay notificaciones" />}
        {notificationIds && (
          <>
            <NotificationFilters
              type={internalNotificationType}
              onChange={setInternalNotificationType}
            />
            {loading && <Loading />}
            {!!notificationIds?.length && !loading && (
              <ul
                className={css({
                  margin: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                  height: '100%',
                  flex: 1,
                })}
              >
                <AutoSizer>
                  {({ width, height }) => (
                    <InfiniteLoader
                      isItemLoaded={isItemLoaded}
                      itemCount={itemCount}
                      loadMoreItems={loadMoreItems}
                    >
                      {({ onItemsRendered, ref }) => (
                        <List
                          height={height ? height : 500}
                          itemCount={itemCount}
                          itemSize={() => 96}
                          width={width ? width : 200}
                          itemData={{
                            onClose,
                            items: notificationIds,
                            isLoaded: isItemLoaded,
                          }}
                          onItemsRendered={onItemsRendered}
                          overscanCount={4}
                          ref={ref}
                        >
                          {NotificationItem}
                        </List>
                      )}
                    </InfiniteLoader>
                  )}
                </AutoSizer>
              </ul>
            )}
            {!notificationIds.length && !loading && (
              <EmptyState
                title="No se encontraron resultados"
                description="Intenta con otro filtro"
              />
            )}
          </>
        )}
      </FlexColumn>
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteAll}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar todas las notificaciones?</ParagraphMedium>}
      />
    </FlexColumn>
  );
}
